import { notify } from '@affine/component';
import {
  type PermissionLifecycleAreaImpact,
  PermissionLifecycleOperation,
  PermissionPreflightTargetKind,
} from '@affine/graphql';
import { I18n } from '@affine/i18n';
import { Service } from '@toeverything/infra';

import type { AccessibleDocCacheStore } from '../../doc/stores/accessible-doc-cache';
import {
  OfflineMutationOutboxService,
  OfflineMutationTransientError,
} from '../../offline-mutation-outbox/services/offline-mutation-outbox';
import type {
  FolderAddDocMutationPayload,
  FolderAddDocToRootMutationPayload,
  FolderCreateMutationPayload,
  FolderMoveMutationPayload,
  FolderRenameMutationPayload,
  OfflineMutationRecord,
  PermissionGraphMutationMetadata,
} from '../../offline-mutation-outbox/types';
import type { AccessibleFolderCacheStore } from '../../organize/stores/accessible-folder-cache';
import type { WorkspaceService } from '../../workspace/services/workspace';
import type { FolderApiService } from './folder-api';

export class FolderMutationOutboxService extends Service {
  private readonly folderCreateEnqueues = new Map<string, Promise<unknown>>();

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly folderApiService: FolderApiService,
    private readonly outbox: OfflineMutationOutboxService,
    private readonly accessibleFolderCache: AccessibleFolderCacheStore,
    private readonly accessibleDocCache: AccessibleDocCacheStore
  ) {
    super();
    this.registerExecutors();
    const failedSubscription = this.outbox.failed$.subscribe(mutation => {
      if (mutation.workspaceId !== this.workspaceService.workspace.id) {
        return;
      }
      if (mutation.operation.startsWith('folder.')) {
        this.accessibleFolderCache.rejectPendingFolderMutation(
          mutation.operation,
          mutation.payload as { folderId?: string }
        );
        if (
          mutation.operation === 'folder.addDoc' ||
          mutation.operation === 'folder.addDocToRoot'
        ) {
          const payload = mutation.payload as FolderAddDocMutationPayload;
          this.accessibleFolderCache.clearPendingDocFolderAssignment(
            payload.docId
          );
        }
        this.refreshMutationCaches(
          mutation.payload as PermissionGraphMutationMetadata
        ).catch(() => {});
        if (mutation.operation === 'folder.move') {
          notify.error({
            title:
              I18n[
                'com.streamlain.rootAppSidebar.organize.folder.error.move-folder-failed'
              ](),
          });
        } else if (
          mutation.operation === 'folder.addDoc' ||
          mutation.operation === 'folder.addDocToRoot' ||
          mutation.operation === 'folder.removeDoc'
        ) {
          notify.error({
            title:
              I18n[
                'com.streamlain.rootAppSidebar.organize.folder.error.move-doc-failed'
              ](),
          });
        }
      }
    });
    this.disposables.push(() => failedSubscription.unsubscribe());
  }

  enqueueCreateFolder(payload: FolderCreateMutationPayload): Promise<unknown> {
    const affectedFolderIds = [payload.folderId];
    if (payload.parentId) {
      affectedFolderIds.push(payload.parentId);
    }
    const enqueue = this.outbox.enqueue({
      operation: 'folder.create',
      payload: { ...payload, affectedFolderIds },
      idempotencyKey: `folder.create:${payload.folderId}`,
    });
    this.folderCreateEnqueues.set(payload.folderId, enqueue);
    const clear = () => {
      if (this.folderCreateEnqueues.get(payload.folderId) === enqueue) {
        this.folderCreateEnqueues.delete(payload.folderId);
      }
    };
    void enqueue.then(clear, clear);
    return enqueue;
  }

  enqueueRenameFolder(folderId: string, name: string): Promise<unknown> {
    return this.outbox.enqueue({
      operation: 'folder.rename',
      payload: { folderId, name, affectedFolderIds: [folderId] },
      idempotencyKey: `folder.rename:${folderId}:${name}`,
    });
  }

  enqueueMoveFolder(
    folderId: string,
    parentId: string | null
  ): Promise<unknown> {
    return this.outbox.enqueue({
      operation: 'folder.move',
      payload: {
        folderId,
        parentId,
        affectedFolderIds: parentId ? [folderId, parentId] : [folderId],
        rollback: {
          folderIds: parentId ? [folderId, parentId] : [folderId],
        },
      },
      idempotencyKey: `folder.move:${folderId}:${parentId ?? 'root'}`,
    });
  }

  enqueueUpdateFolderSettings(
    folderId: string,
    data: { visibility?: string; inheritPermissions?: boolean }
  ): Promise<unknown> {
    return this.folderApiService
      .updateFolder(this.workspaceService.workspace.id, folderId, {
        ...(data.visibility !== undefined && {
          visibility: data.visibility,
        }),
        ...(data.inheritPermissions !== undefined && {
          inheritPermissions: data.inheritPermissions,
        }),
      })
      .then(folder => {
        this.accessibleFolderCache.applyServerConfirmedFolderSettings(
          folderId,
          {
            visibility: folder.visibility,
            inheritPermissions: folder.inheritPermissions,
            updatedAt: folder.updatedAt,
          }
        );
        return folder;
      });
  }

  async enqueueAddDocToFolder(
    folderId: string,
    docId: string
  ): Promise<OfflineMutationRecord<'folder.addDoc'>> {
    await this.folderCreateEnqueues.get(folderId);
    return this.outbox.enqueue({
      operation: 'folder.addDoc',
      payload: {
        folderId,
        docId,
        affectedFolderIds: [folderId],
        affectedDocIds: [docId],
        rollback: { folderIds: [folderId], docIds: [docId] },
      },
      idempotencyKey: `folder.addDoc:${folderId}:${docId}`,
      dedupePending: true,
    });
  }

  enqueueAddDocToWorkspaceRoot(docId: string): Promise<unknown> {
    const rootFolderId =
      this.accessibleFolderCache.getCachedWorkspaceRootFolderId();
    return this.outbox.enqueue({
      operation: 'folder.addDocToRoot',
      payload: {
        docId,
        affectedFolderIds: rootFolderId ? [rootFolderId] : [],
        affectedDocIds: [docId],
        rollback: {
          folderIds: rootFolderId ? [rootFolderId] : [],
          docIds: [docId],
        },
      },
      idempotencyKey: `folder.addDocToRoot:${docId}`,
      dedupePending: true,
    });
  }

  enqueueRemoveDocFromFolder(docId: string): Promise<unknown> {
    const rootFolderId =
      this.accessibleFolderCache.getCachedWorkspaceRootFolderId();
    return this.outbox.enqueue({
      operation: 'folder.removeDoc',
      payload: {
        docId,
        affectedFolderIds: rootFolderId ? [rootFolderId] : [],
        affectedDocIds: [docId],
        rollback: {
          folderIds: rootFolderId ? [rootFolderId] : [],
          docIds: [docId],
        },
      },
      idempotencyKey: `folder.removeDoc:${docId}`,
      dedupePending: true,
    });
  }

  private async resolveWorkspaceRootFolderId(): Promise<string | null> {
    const cachedRootId =
      this.accessibleFolderCache.getCachedWorkspaceRootFolderId();
    if (cachedRootId || this.workspaceService.workspace.flavour === 'local') {
      return cachedRootId;
    }
    return (
      (
        await this.folderApiService.getRootFolder(
          this.workspaceService.workspace.id
        )
      )?.id ?? null
    );
  }

  private async preflightDocMove(
    docId: string,
    targetFolderId: string | null
  ): Promise<PermissionLifecycleAreaImpact[]> {
    if (this.workspaceService.workspace.flavour === 'local') {
      return [];
    }
    if (!targetFolderId) {
      throw new OfflineMutationTransientError(
        'Workspace root folder is not available yet'
      );
    }
    const result = await this.folderApiService.preflightPermissionLifecycle({
      operation: PermissionLifecycleOperation.DOC_MOVE,
      targets: [
        {
          kind: PermissionPreflightTargetKind.DOC,
          id: docId,
          targetFolderId,
        },
      ],
      workspaceId: this.workspaceService.workspace.id,
    });
    if (!result.allowed) {
      throw new Error('Doc lifecycle preflight denied');
    }
    return result.areaImpacts ?? [];
  }

  private async preflightFolderMove(
    folderId: string,
    targetFolderId: string | null
  ): Promise<PermissionLifecycleAreaImpact[]> {
    if (this.workspaceService.workspace.flavour === 'local') {
      return [];
    }
    const result = await this.folderApiService.preflightPermissionLifecycle({
      operation: PermissionLifecycleOperation.FOLDER_MOVE,
      targets: [
        {
          kind: PermissionPreflightTargetKind.FOLDER,
          id: folderId,
          targetFolderId,
        },
      ],
      workspaceId: this.workspaceService.workspace.id,
    });
    if (!result.allowed) {
      throw new Error('Folder lifecycle preflight denied');
    }
    return result.areaImpacts ?? [];
  }

  private notifyBlockedAreaImpacts(
    areaImpacts: PermissionLifecycleAreaImpact[]
  ): void {
    const blockedPrincipals = new Set(
      areaImpacts
        .filter(
          impact => impact.impact === 'BECOMES_BLOCKED' && impact.toAreaBlocked
        )
        .map(impact => `${impact.principalType}:${impact.principalId}`)
    );
    if (blockedPrincipals.size === 0) {
      return;
    }
    notify.success({
      title: I18n['com.streamlain.folder-move.area-impact.title'](),
      message: I18n['com.streamlain.folder-move.area-impact.blocked']({
        count: String(blockedPrincipals.size),
      }),
    });
  }

  private registerExecutors(): void {
    this.disposables.push(
      this.outbox.registerExecutor('folder.create', async mutation => {
        const payload = mutation.payload as FolderCreateMutationPayload;
        let recoveredExistingFolder = false;
        try {
          await this.folderApiService.createFolder(
            mutation.workspaceId,
            payload.name,
            payload.parentId,
            {
              id: payload.folderId,
              visibility: payload.visibility,
              inheritPermissions: payload.inheritPermissions,
            }
          );
        } catch (error) {
          if (!(await this.isCreateFolderAlreadyMaterialized(payload))) {
            throw error;
          }
          recoveredExistingFolder = true;
        }
        // Normal creates reconcile through the same server event as every
        // other tab. Only retry recovery lacks a guaranteed event delivery.
        if (recoveredExistingFolder) {
          await this.refreshMutationCaches(payload);
        }
      }),
      this.outbox.registerExecutor('folder.move', async mutation => {
        const payload = mutation.payload as FolderMoveMutationPayload;
        const permissionPreflight = this.preflightFolderMove(
          payload.folderId,
          payload.parentId
        );
        await this.folderApiService.moveFolder(
          mutation.workspaceId,
          payload.folderId,
          payload.parentId,
          permissionPreflight
        );
        const areaImpacts = await permissionPreflight;
        // The optimistic tree move is already visible locally. The
        // recipient-filtered folder_moved event owns authoritative scoped
        // reconciliation for this tab and every other client.
        this.notifyBlockedAreaImpacts(areaImpacts);
      }),
      this.outbox.registerExecutor('folder.rename', async mutation => {
        const payload = mutation.payload as FolderRenameMutationPayload;
        await this.folderApiService.renameFolder(
          mutation.workspaceId,
          payload.folderId,
          payload.name,
          null
        );
      }),
      this.outbox.registerExecutor('folder.addDoc', async mutation => {
        const payload = mutation.payload as FolderAddDocMutationPayload;
        await this.ensureDocServerReady(payload.docId);
        const permissionPreflight = this.preflightDocMove(
          payload.docId,
          payload.folderId
        );
        await this.folderApiService.addDocToFolder(
          mutation.workspaceId,
          payload.folderId,
          payload.docId,
          permissionPreflight
        );
        const areaImpacts = await permissionPreflight;
        this.notifyBlockedAreaImpacts(areaImpacts);
      }),
      this.outbox.registerExecutor('folder.addDocToRoot', async mutation => {
        const payload = mutation.payload as FolderAddDocToRootMutationPayload;
        const rootFolderId = await this.resolveWorkspaceRootFolderId();
        await this.ensureDocServerReady(payload.docId);
        const permissionPreflight = this.preflightDocMove(
          payload.docId,
          rootFolderId
        );
        if (!rootFolderId) {
          throw new OfflineMutationTransientError(
            'Workspace root folder is not available yet'
          );
        }
        await this.folderApiService.addDocToFolder(
          mutation.workspaceId,
          rootFolderId,
          payload.docId,
          permissionPreflight
        );
        const areaImpacts = await permissionPreflight;
        this.notifyBlockedAreaImpacts(areaImpacts);
      }),
      this.outbox.registerExecutor('folder.removeDoc', async mutation => {
        const rootFolderId = await this.resolveWorkspaceRootFolderId();
        await this.ensureDocServerReady(mutation.payload.docId);
        const areaImpacts = await this.preflightDocMove(
          mutation.payload.docId,
          rootFolderId
        );
        try {
          await this.folderApiService.removeDocFromFolder(
            mutation.workspaceId,
            mutation.payload.docId
          );
        } catch (error) {
          if (!isNotFoundError(error)) {
            throw error;
          }
        }
        await this.refreshMutationCaches(
          mutation.payload as PermissionGraphMutationMetadata
        );
        this.notifyBlockedAreaImpacts(areaImpacts);
      })
    );
  }

  private async ensureDocServerReady(docId: string): Promise<void> {
    if (!this.accessibleDocCache.isLocallyCreated(docId)) {
      return;
    }

    const docEngine = this.workspaceService.workspace.engine?.doc;
    if (docEngine?.flushAndWaitForSynced) {
      await docEngine.flushAndWaitForSynced(this.workspaceService.workspace.id);
      await docEngine.flushAndWaitForSynced(docId);
    }

    const confirmed = await this.accessibleDocCache.waitForServerConfirmed(
      docId,
      {
        timeoutMs: 10000,
        pollMs: 250,
      }
    );
    if (!confirmed) {
      throw new OfflineMutationTransientError(
        'Doc server confirmation pending'
      );
    }
  }

  private async refreshMutationCaches(
    payload: PermissionGraphMutationMetadata
  ): Promise<void> {
    if (this.workspaceService.workspace.flavour === 'local') {
      return;
    }
    const affectedFolderIds = payload.affectedFolderIds ?? [];
    const affectedDocIds = payload.affectedDocIds ?? [];
    const shouldRefreshFolders = affectedFolderIds.length > 0;
    const shouldRefreshDocs = affectedDocIds.length > 0;
    const tasks: Promise<unknown>[] = [];

    if (shouldRefreshFolders) {
      tasks.push(this.accessibleFolderCache.fetchAccessibleFolders());
    }
    if (shouldRefreshFolders || shouldRefreshDocs) {
      tasks.push(this.accessibleFolderCache.refreshRootFolderDocIds());
    }
    if (shouldRefreshDocs) {
      tasks.push(this.accessibleDocCache.fetchAccessibleDocs());
    }

    if (tasks.length > 0) {
      await Promise.allSettled(tasks);
    }
  }

  private async isCreateFolderAlreadyMaterialized(
    payload: FolderCreateMutationPayload
  ): Promise<boolean> {
    try {
      await this.accessibleFolderCache.fetchAccessibleFolders();
    } catch {
      return false;
    }

    const folder = this.accessibleFolderCache.getCachedFolder(payload.folderId);
    if (!folder || folder.metadataOnly) {
      return false;
    }
    if (folder.name !== payload.name || folder.parentId !== payload.parentId) {
      return false;
    }
    if (
      payload.visibility !== undefined &&
      folder.visibility !== payload.visibility
    ) {
      return false;
    }
    if (
      payload.inheritPermissions !== undefined &&
      folder.inheritPermissions !== payload.inheritPermissions
    ) {
      return false;
    }
    return true;
  }
}

function isNotFoundError(error: unknown): boolean {
  if (errorStatus(error) === 404) {
    return true;
  }
  return errorDiagnosticText(error).toLowerCase().includes('not found');
}

function errorStatus(error: unknown): number {
  if (!error || typeof error !== 'object') {
    return 0;
  }
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : 0;
}

function errorDiagnosticText(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (!error || typeof error !== 'object') {
    return '';
  }
  const values = [
    (error as { message?: unknown }).message,
    (error as { code?: unknown }).code,
    (error as { type?: unknown }).type,
    (error as { name?: unknown }).name,
  ];
  return values.filter(value => typeof value === 'string').join(' ');
}
